import { useEffect, useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { ImageOutlined } from '@mui/icons-material';
import { FinishOrderLayout } from '../../../orders/usecases/finish/FinishOrderLayout';
import {
  DeliveryConfiguration,
  createDeliveryValue,
} from '../../../orders/usecases/finish/deliveryConfiguration';
import { useProductImage } from '../../../orders/pages/newOrderFlow/configureEquipmentMockup/productApi';
import { usePortalStore } from '../../../../stores/portalStore';
import { formatCPF, formatCNPJ } from '../../../../utils/format';
import type { Product } from '../../../../types/product/Product';
import type { PatientV2Detail } from './types';
import {
  groupReviewExtras,
  PatientEquipmentConfiguration,
} from './patientEquipmentConfiguration';

const display = (value: unknown) =>
  value == null || String(value).trim() === '' ? '—' : String(value);
const ReviewImage = ({ code, name }: { code: string; name: string }) => {
  const src = useProductImage(code);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return (
    <Box
      sx={{
        width: '100%',
        height: 160,
        bgcolor: '#f7f9f7',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {src && !failed ? (
        <Box
          component="img"
          src={src}
          alt={name}
          onError={() => setFailed(true)}
          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      ) : (
        <ImageOutlined
          aria-label="Imagem indisponível"
          sx={{ fontSize: 36, color: '#a7b6aa' }}
        />
      )}
    </Box>
  );
};

export const PatientEquipmentReview = ({
  patient,
  product,
  configuration,
  onBack,
}: {
  patient: PatientV2Detail;
  product: Product;
  configuration: PatientEquipmentConfiguration;
  onBack: () => void;
}) => {
  const currentUser = usePortalStore(
    (state) =>
      (state as { currentUser: { name?: string; email?: string } }).currentUser
  );
  const [delivery, setDelivery] = useState(createDeliveryValue);
  const [notes, setNotes] = useState('');
  const extras = groupReviewExtras(configuration.items, product.sap_code);
  const summary = [
    ['Paciente', patient.patient],
    ['CPF', patient.cpf ? formatCPF(patient.cpf) : undefined],
    ['Empresa', patient.company?.empresa],
    [
      'CNPJ',
      patient.company?.cnpj
        ? formatCNPJ(String(patient.company.cnpj))
        : undefined,
    ],
    ['Solicitante', currentUser?.name || currentUser?.email],
  ];
  const address = [
    ['Cidade', patient.city],
    ['Endereço', patient.address],
    ['Número', patient.number],
    ['CEP', patient.cep],
    ['Complemento', patient.addressLine2],
    ['Bairro', patient.neighborhood],
    ['UF', patient.uf],
  ];
  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <Button type="button" onClick={onBack} sx={{ alignSelf: 'flex-start' }}>
        Voltar às perguntas
      </Button>
      <FinishOrderLayout
        embedded
        title="Revisão dos equipamentos"
        summary={
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              bgcolor: '#f7f7f7',
              borderRadius: 1,
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px 24px',
              overflowWrap: 'anywhere',
            }}
          >
            {summary.map(([label, value]) => (
              <Typography key={label} variant="body2">
                <strong>{label}:</strong> {display(value)}
              </Typography>
            ))}
          </Box>
        }
        delivery={
          <Box
            sx={{
              width: '100%',
              minWidth: 0,
              '& .react-calendar': {
                width: '100%',
                maxWidth: '100%',
                fontFamily: 'inherit',
              },
              '& input': { flexShrink: 0 },
            }}
          >
            <DeliveryConfiguration
              selectedPatient={patient}
              orderType="Implantação"
              fastDeliveryLabel="Entrega imediata"
              scheduleDeliveryLabel="Entrega agendada"
              _hasFastDelivery
              value={delivery}
              onChange={setDelivery}
              showFinishAction={false}
              addressRender={() => (
                <Box
                  component="dl"
                  sx={{
                    bgcolor: '#f7f7f7',
                    p: 1.25,
                    m: '0 0 12px',
                    borderRadius: 1,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {address.map(([label, value]) => (
                    <Box
                      key={label}
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        fontSize: 14,
                        mb: 0.5,
                      }}
                    >
                      <Box component="dt" sx={{ fontWeight: 700 }}>
                        {label}:
                      </Box>
                      <Box component="dd" sx={{ m: 0 }}>
                        {display(value)}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            />
          </Box>
        }
        equipment={
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  sm: 'minmax(120px, 180px) minmax(0, 1fr)',
                },
                gap: 2,
                alignItems: 'center',
              }}
            >
              <ReviewImage code={product.sap_code} name={product.description} />
              <Box sx={{ overflowWrap: 'anywhere' }}>
                <Typography component="h4" fontWeight={700}>
                  {product.description}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Equipamento principal
                </Typography>
              </Box>
            </Box>
            <Typography component="h4" fontWeight={700}>
              Acessórios escolhidos
            </Typography>
            {extras.length ? (
              extras.map((item) => (
                <Box
                  key={item.sapCode}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '80px minmax(0, 1fr)',
                    alignItems: 'center',
                    gap: 1.5,
                    '& > div:first-of-type': { height: 80 },
                  }}
                >
                  <ReviewImage code={item.sapCode} name={item.name} />
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    <strong>{item.quantity}x</strong> {item.name}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhum acessório selecionado.
              </Typography>
            )}
          </Stack>
        }
        notes={
          <TextField
            label="Informações adicionais"
            multiline
            minRows={4}
            fullWidth
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        }
      />
      <Typography variant="body2" color="text.secondary">
        Configuração, agendamento e observações ficam apenas nesta tela e ainda
        não serão salvos no cadastro.
      </Typography>
    </Stack>
  );
};
